import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { useT } from '@/i18n'
import { cn } from '@/lib/utils'

export type ToastTone = 'info' | 'success' | 'warning' | 'destructive'

export type Toast = {
  id: string
  title: string
  description?: string
  tone: ToastTone
}

type ToastInput = Omit<Toast, 'id' | 'tone'> & { tone?: ToastTone; durationMs?: number }

type ToastContextValue = {
  toast: (input: ToastInput) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TONE_ICON: Record<ToastTone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: XCircle,
}

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'border-l-info',
  success: 'border-l-success',
  warning: 'border-l-warning',
  destructive: 'border-l-destructive',
}

const DEFAULT_DURATION_MS = 6000

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const t = useT()
  const [toasts, setToasts] = useState<Toast[]>([])
  const counter = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((item) => item.id !== id))
  }, [])

  const toast = useCallback(
    ({ tone = 'info', durationMs = DEFAULT_DURATION_MS, ...rest }: ToastInput) => {
      counter.current += 1
      const id = `toast-${counter.current}`
      setToasts((current) => [...current, { id, tone, ...rest }])
      window.setTimeout(() => dismiss(id), durationMs)
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Assertive only for failures; everything else is polite so it does not
          interrupt a screen reader mid-sentence. */}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
        role="region"
        aria-label={t('topbar.notifications')}
      >
        {toasts.map((item) => {
          const Icon = TONE_ICON[item.tone]
          return (
            <div
              key={item.id}
              role={item.tone === 'destructive' ? 'alert' : 'status'}
              aria-live={item.tone === 'destructive' ? 'assertive' : 'polite'}
              className={cn(
                'animate-panel-in pointer-events-auto flex w-full max-w-sm items-start gap-3',
                'rounded-md border border-border border-l-4 bg-popover p-3 text-popover-foreground shadow-3',
                TONE_CLASS[item.tone],
              )}
            >
              <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-body-sm font-medium">{item.title}</p>
                {item.description ? (
                  <p className="mt-0.5 text-caption text-muted-foreground">{item.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label={t('common.close')}
                className="-m-1 rounded-sm p-1 text-muted-foreground transition-colors duration-hover hover:text-foreground"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a <ToastProvider>')
  return context
}
